/**
 * Maps tool names to i18n keys for loading labels.
 * These are shown while the tool is being invoked (before results arrive).
 * Consuming components must translate via t() since this is a utility module.
 */
const TOOL_LABELS: Record<string, string> = {
  // Knowledge base tools
  listArticlesInCategory: 'optibot.tool.searchingKB',
  readArticle: 'optibot.tool.readingArticle',
  searchArticles: 'optibot.tool.searchingArticles',
  // Screen tools
  getScreensSummary: 'optibot.tool.gettingScreensSummary',
  listScreens: 'optibot.tool.lookingUpScreens',
  getScreen: 'optibot.tool.gettingScreenDetails',
  searchScreens: 'optibot.tool.searchingScreens',
  // Asset tools
  listAssets: 'optibot.tool.lookingUpAssets',
  getAsset: 'optibot.tool.gettingAssetDetails',
  searchAssets: 'optibot.tool.searchingAssets',
  // Playlist tools
  listPlaylists: 'optibot.tool.lookingUpPlaylists',
  getPlaylist: 'optibot.tool.gettingPlaylistDetails',
  searchPlaylists: 'optibot.tool.searchingPlaylists',
  // Schedule tools
  listSchedules: 'optibot.tool.lookingUpSchedules',
  getSchedule: 'optibot.tool.gettingScheduleDetails',
  searchSchedules: 'optibot.tool.searchingSchedules',
  // Write tools
  rebootScreen: 'optibot.tool.rebootingScreen',
  refreshScreen: 'optibot.tool.refreshingScreen',
  clearScreenCache: 'optibot.tool.clearingScreenCache',
  pushToScreens: 'optibot.tool.pushingContent',
  deleteAsset: 'optibot.tool.deletingAsset',
  deletePlaylist: 'optibot.tool.deletingPlaylist',
  assignPlaylistToScreens: 'optibot.tool.assigningPlaylist',
  deleteSchedule: 'optibot.tool.deletingSchedule',
  updateScreen: 'optibot.tool.updatingScreen',
  renameAsset: 'optibot.tool.renamingAsset',
  updateAssetTags: 'optibot.tool.updatingAssetTags',
  renamePlaylist: 'optibot.tool.renamingPlaylist',
  addPlaylistItems: 'optibot.tool.addingPlaylistItems',
  removePlaylistItems: 'optibot.tool.removingPlaylistItems',
  renameSchedule: 'optibot.tool.renamingSchedule',
  addScheduleItem: 'optibot.tool.addingScheduleItem',
  removeScheduleItem: 'optibot.tool.removingScheduleItem',
  setScheduleDefault: 'optibot.tool.settingScheduleDefault',
  // Account tools
  getMyProfile: 'optibot.tool.gettingProfile',
  listTeamMembers: 'optibot.tool.lookingUpTeamMembers',
  listTeams: 'optibot.tool.lookingUpTeams',
  getUserPreferences: 'optibot.tool.gettingPreferences',
  listIntegrations: 'optibot.tool.lookingUpIntegrations',
  // Admin tools
  getSamlConfig: 'optibot.tool.checkingSSOConfig',
  listMonitoringAlerts: 'optibot.tool.lookingUpAlerts',
  getMonitoringAlert: 'optibot.tool.gettingAlertDetails',
  listProvisioningTemplates: 'optibot.tool.lookingUpTemplates',
  listOperationalSchedules: 'optibot.tool.lookingUpOpsSchedules',
  // Feed tools
  searchFeeds: 'optibot.tool.searchingFeeds',
  listFeedCategories: 'optibot.tool.lookingUpFeedCategories',
  // Folder/group tools
  listFolders: 'optibot.tool.lookingUpFolders',
  getFolderInfo: 'optibot.tool.gettingFolderDetails',
  searchFolders: 'optibot.tool.searchingFolders',
  // MDM ops tools
  getDeviceInfo: 'optibot.tool.gettingDeviceInfo',
  getDeviceStorage: 'optibot.tool.checkingStorage',
  identifyScreen: 'optibot.tool.identifyingScreen',
  // Location tools
  listLocations: 'optibot.tool.lookingUpLocations',
  getLocationDevices: 'optibot.tool.gettingLocationDevices',
  // App discovery tools
  listAppTypes: 'optibot.tool.browsingApps',
  getAppTypeInfo: 'optibot.tool.gettingAppDetails',
  // Template tools
  searchTemplates: 'optibot.tool.browsingTemplates',
  // AI Designer tools
  generateDesign: 'optibot.tool.generatingDesign',
  // Billing tools
  getBillingOverview: 'optibot.tool.checkingBilling',
  listInvoices: 'optibot.tool.lookingUpInvoices',
  listUnpaidInvoices: 'optibot.tool.checkingUnpaidInvoices',
  getPaymentMethod: 'optibot.tool.checkingPaymentMethod',
  listAddOns: 'optibot.tool.lookingUpAddOns',
  getCustomerCoupon: 'optibot.tool.checkingCoupon',
  listTaxIds: 'optibot.tool.lookingUpTaxIds',
  listSubAccounts: 'optibot.tool.lookingUpSubAccounts',
  listPlanTiers: 'optibot.tool.lookingUpPlanTiers',
  getAddOnCatalog: 'optibot.tool.lookingUpAddOnCatalog',
  // Billing write tools (Tier 2)
  updateBillingEmail: 'optibot.tool.updatingBillingEmail',
  updateBillingAddress: 'optibot.tool.updatingBillingAddress',
  sendInvoiceByEmail: 'optibot.tool.sendingInvoice',
  addTaxId: 'optibot.tool.addingTaxId',
  deleteTaxId: 'optibot.tool.deletingTaxId',
  // Billing write tools (Tier 3)
  getSubscriptionChangePreview: 'optibot.tool.previewingSubscriptionChange',
  increaseScreenCount: 'optibot.tool.increasingScreenCount',
  decreaseScreenCount: 'optibot.tool.decreasingScreenCount',
  changeBillingInterval: 'optibot.tool.changingBillingInterval',
  switchPlanTier: 'optibot.tool.switchingPlanTier',
  buyAddOn: 'optibot.tool.purchasingAddOn',
  startAddOnTrial: 'optibot.tool.startingAddOnTrial',
  cancelAddOnTrial: 'optibot.tool.cancellingAddOnTrial',
  resumeSubscription: 'optibot.tool.resumingSubscription',
  payUnpaidInvoices: 'optibot.tool.payingInvoices',
};

/**
 * Set of tool names that perform write operations and require user approval.
 */
const WRITE_TOOL_NAMES = new Set([
  'rebootScreen',
  'refreshScreen',
  'clearScreenCache',
  'pushToScreens',
  'updateScreen',
  'renameAsset',
  'updateAssetTags',
  'deleteAsset',
  'renamePlaylist',
  'addPlaylistItems',
  'removePlaylistItems',
  'deletePlaylist',
  'assignPlaylistToScreens',
  'renameSchedule',
  'addScheduleItem',
  'removeScheduleItem',
  'setScheduleDefault',
  'deleteSchedule',
  'identifyScreen',
  'updateBillingEmail',
  'updateBillingAddress',
  'sendInvoiceByEmail',
  'addTaxId',
  'deleteTaxId',
  'increaseScreenCount',
  'decreaseScreenCount',
  'changeBillingInterval',
  'switchPlanTier',
  'buyAddOn',
  'startAddOnTrial',
  'cancelAddOnTrial',
  'resumeSubscription',
  'payUnpaidInvoices',
]);

/**
 * Returns true if the tool is a write tool that requires user approval.
 */
export function isWriteTool(toolName: string): boolean {
  return WRITE_TOOL_NAMES.has(toolName);
}

/**
 * i18n keys for write tool display names (used in ApprovalCard header).
 * Consuming components must translate via t().
 */
const WRITE_TOOL_DISPLAY_NAMES: Record<string, string> = {
  rebootScreen: 'optibot.tool.rebootScreen',
  refreshScreen: 'optibot.tool.refreshScreen',
  clearScreenCache: 'optibot.tool.clearScreenCache',
  pushToScreens: 'optibot.tool.pushToScreens',
  deleteAsset: 'optibot.tool.deleteAsset',
  deletePlaylist: 'optibot.tool.deletePlaylist',
  assignPlaylistToScreens: 'optibot.tool.assignPlaylistToScreens',
  deleteSchedule: 'optibot.tool.deleteSchedule',
  updateScreen: 'optibot.tool.updateScreen',
  renameAsset: 'optibot.tool.renameAsset',
  updateAssetTags: 'optibot.tool.updateAssetTags',
  renamePlaylist: 'optibot.tool.renamePlaylist',
  addPlaylistItems: 'optibot.tool.addPlaylistItems',
  removePlaylistItems: 'optibot.tool.removePlaylistItems',
  renameSchedule: 'optibot.tool.renameSchedule',
  addScheduleItem: 'optibot.tool.addScheduleItem',
  removeScheduleItem: 'optibot.tool.removeScheduleItem',
  setScheduleDefault: 'optibot.tool.setScheduleDefault',
  identifyScreen: 'optibot.tool.identifyScreen',
  updateBillingEmail: 'optibot.tool.updateBillingEmail',
  updateBillingAddress: 'optibot.tool.updateBillingAddress',
  sendInvoiceByEmail: 'optibot.tool.sendInvoiceByEmail',
  addTaxId: 'optibot.tool.addTaxId',
  deleteTaxId: 'optibot.tool.deleteTaxId',
  increaseScreenCount: 'optibot.tool.increaseScreenCount',
  decreaseScreenCount: 'optibot.tool.decreaseScreenCount',
  changeBillingInterval: 'optibot.tool.changeBillingInterval',
  switchPlanTier: 'optibot.tool.switchPlanTier',
  buyAddOn: 'optibot.tool.buyAddOn',
  startAddOnTrial: 'optibot.tool.startAddOnTrial',
  cancelAddOnTrial: 'optibot.tool.cancelAddOnTrial',
  resumeSubscription: 'optibot.tool.resumeSubscription',
  payUnpaidInvoices: 'optibot.tool.payUnpaidInvoices',
};

/**
 * Returns an i18n key for a write tool's display name.
 * Consuming components must translate the returned key via t().
 */
export function getWriteToolDisplayName(toolName: string): string {
  return WRITE_TOOL_DISPLAY_NAMES[toolName] ?? toolName;
}

/** Translate function type accepted by formatToolArgs */
type TFunction = (key: string, options?: Record<string, unknown>) => string;

/**
 * Formats tool arguments into a brief human-readable summary.
 * Accepts a `t` function from the calling component for i18n interpolation.
 * Uses keys: optibot.tool.argScreen, argAsset, argPlaylist, argSchedule,
 * argScreenCount, argContent (defined in public/i18n/*.json).
 */
export function formatToolArgs(
  toolName: string,
  args: Record<string, unknown>,
  t: TFunction,
): string {
  // Screen tools: show screenId or count of screenIds
  if (
    toolName === 'rebootScreen' ||
    toolName === 'refreshScreen' ||
    toolName === 'clearScreenCache' ||
    toolName === 'updateScreen' ||
    toolName === 'identifyScreen'
  ) {
    const screenId = args.screenId;
    if (typeof screenId === 'string') {
      return t('optibot.tool.argScreen', { id: screenId });
    }
    return '';
  }

  // Push to screens: show content + screen count
  if (toolName === 'pushToScreens' || toolName === 'assignPlaylistToScreens') {
    const parts: string[] = [];
    const screenIds = args.screenIds;
    if (Array.isArray(screenIds)) {
      parts.push(t('optibot.tool.argScreenCount', { count: screenIds.length }));
    }
    const contentLabel = args.playlistName ?? args.contentId ?? args.assetId ?? args.playlistId;
    if (typeof contentLabel === 'string') {
      parts.push(t('optibot.tool.argContent', { id: contentLabel }));
    }
    return parts.join(' | ');
  }

  // Asset write tools
  if (toolName === 'renameAsset' || toolName === 'updateAssetTags') {
    const assetId = args.assetId;
    if (typeof assetId === 'string') return t('optibot.tool.argAsset', { id: assetId });
    return '';
  }

  // Playlist write tools
  if (
    toolName === 'renamePlaylist' ||
    toolName === 'addPlaylistItems' ||
    toolName === 'removePlaylistItems'
  ) {
    const playlistName = args.playlistName;
    const playlistId = args.playlistId;
    if (typeof playlistName === 'string')
      return t('optibot.tool.argPlaylist', { id: playlistName });
    if (typeof playlistId === 'string') return t('optibot.tool.argPlaylist', { id: playlistId });
    return '';
  }

  // Schedule write tools
  if (
    toolName === 'renameSchedule' ||
    toolName === 'addScheduleItem' ||
    toolName === 'removeScheduleItem' ||
    toolName === 'setScheduleDefault'
  ) {
    const scheduleId = args.scheduleId;
    if (typeof scheduleId === 'string') return t('optibot.tool.argSchedule', { id: scheduleId });
    return '';
  }

  // Delete tools: show entity ID
  if (toolName === 'deleteAsset') {
    const assetId = args.assetId;
    if (typeof assetId === 'string') return t('optibot.tool.argAsset', { id: assetId });
    return '';
  }
  if (toolName === 'deletePlaylist') {
    const playlistName = args.playlistName;
    const playlistId = args.playlistId;
    if (typeof playlistName === 'string')
      return t('optibot.tool.argPlaylist', { id: playlistName });
    if (typeof playlistId === 'string') return t('optibot.tool.argPlaylist', { id: playlistId });
    return '';
  }
  if (toolName === 'deleteSchedule') {
    const scheduleId = args.scheduleId;
    if (typeof scheduleId === 'string') return t('optibot.tool.argSchedule', { id: scheduleId });
    return '';
  }

  // Billing write tools
  if (toolName === 'updateBillingEmail') {
    const email = args.email;
    if (typeof email === 'string') return email;
    return '';
  }
  if (toolName === 'updateBillingAddress') {
    const parts: string[] = [];
    if (args.line1) parts.push(String(args.line1));
    if (args.city) parts.push(String(args.city));
    if (args.country) parts.push(String(args.country));
    return parts.join(', ');
  }
  if (toolName === 'sendInvoiceByEmail') {
    const email = args.recipientEmail;
    if (typeof email === 'string') return t('optibot.tool.argRecipient', { email });
    return '';
  }
  if (toolName === 'addTaxId') {
    const type = args.taxType;
    const value = args.taxValue;
    if (type && value) return `${type}: ${value}`;
    return '';
  }
  if (toolName === 'deleteTaxId') {
    const value = args.taxValue;
    const id = args.taxId;
    if (typeof value === 'string' && value) return value;
    if (typeof id === 'string') return id;
    return '';
  }

  // Billing write tools (Tier 3)
  if (toolName === 'increaseScreenCount') {
    const additional = args.additionalScreens;
    const charge = args.previewTodayCharge;
    const monthly = args.previewNewMonthly;
    const credit = args.previewCreditToAccount;
    const parts: string[] = [];
    if (typeof additional === 'number') parts.push(`+${additional} screens`);
    if (typeof charge === 'number' && charge > 0) parts.push(`$${charge.toFixed(2)} today`);
    if (typeof monthly === 'number') parts.push(`$${monthly.toFixed(2)}/mo`);
    if (typeof credit === 'number' && credit > 0)
      parts.push(`Remaining credit: $${credit.toFixed(2)}`);
    return parts.join(' | ');
  }
  if (toolName === 'decreaseScreenCount') {
    const newCount = args.newScreenCount;
    const monthly = args.previewNewMonthly;
    const credit = args.previewCreditToAccount;
    const parts: string[] = [];
    if (typeof newCount === 'number') parts.push(`→ ${newCount} screens`);
    if (typeof monthly === 'number') parts.push(`$${monthly.toFixed(2)}/mo`);
    if (typeof credit === 'number' && credit > 0)
      parts.push(`Credit to account: $${credit.toFixed(2)}`);
    return parts.join(' | ');
  }
  if (toolName === 'changeBillingInterval') {
    const interval = args.newInterval;
    const charge = args.previewTodayCharge;
    const monthly = args.previewNewMonthly;
    const credit = args.previewCreditToAccount;
    const parts: string[] = [];
    if (typeof interval === 'string') parts.push(`→ ${interval}`);
    if (typeof charge === 'number' && charge > 0) parts.push(`$${charge.toFixed(2)} today`);
    if (typeof monthly === 'number') parts.push(`$${monthly.toFixed(2)}/mo`);
    if (typeof credit === 'number' && credit > 0)
      parts.push(`Credit to account: $${credit.toFixed(2)}`);
    return parts.join(' | ');
  }
  if (toolName === 'switchPlanTier') {
    const PLAN_NAMES: Record<string, string> = {
      standard: 'Standard',
      proplus: 'Pro Plus',
      engage: 'Engage',
      enterprise: 'Enterprise',
      free: 'Free',
    };
    const rawPlan = args.newPlanAlias;
    const plan =
      typeof rawPlan === 'string' ? (PLAN_NAMES[rawPlan.toLowerCase()] ?? rawPlan) : null;
    const charge = args.previewTodayCharge;
    const monthly = args.previewNewMonthly;
    const credit = args.previewCreditToAccount;
    const taxIncluded = args.previewTaxIncluded !== false;
    const taxSuffix = taxIncluded ? '' : ' + tax est.';
    const parts: string[] = [];
    if (plan) parts.push(`→ ${plan}`);
    if (typeof charge === 'number' && charge > 0 && charge !== monthly) {
      parts.push(`$${charge.toFixed(2)} today${taxSuffix}`);
    }
    if (typeof monthly === 'number') parts.push(`$${monthly.toFixed(2)}/mo${taxSuffix}`);
    if (typeof credit === 'number' && credit > 0)
      parts.push(`Remaining credit: $${credit.toFixed(2)}`);
    return parts.join(' | ');
  }
  if (toolName === 'buyAddOn') {
    const key = args.addOnKey;
    const qty = args.quantity;
    const est = args.estimatedMonthlyCost;
    const parts: string[] = [];
    if (typeof key === 'string') parts.push(key);
    if (typeof qty === 'number') parts.push(`${qty} screens`);
    if (typeof est === 'number') parts.push(`~$${est.toFixed(2)}/mo + tax est.`);
    return parts.join(' | ');
  }
  if (toolName === 'startAddOnTrial') {
    const key = args.addOnKey;
    if (typeof key === 'string') return key;
    return '';
  }
  if (toolName === 'cancelAddOnTrial') {
    const key = args.addOnKey;
    if (typeof key === 'string') return key;
    return '';
  }
  if (toolName === 'payUnpaidInvoices') {
    const total = args.confirmedTotalAmount;
    if (typeof total === 'number') return `$${total.toFixed(2)}`;
    return '';
  }

  // Default: key=value pairs (truncated)
  const entries = Object.entries(args);
  if (entries.length === 0) return '';
  return entries
    .slice(0, 3)
    .map(([key, value]) => `${key}: ${String(value)}`)
    .join(', ');
}

/**
 * Returns an i18n key for the tool loading label.
 * Consuming components must translate the returned key via t().
 */
export function getToolLabel(toolName: string): string {
  return TOOL_LABELS[toolName] ?? 'optibot.tool.working';
}

/**
 * Completed tool result labels — map toolName to a past-tense i18n key.
 * Shown as a single inline line with a ✓ icon after the tool finishes.
 */
const TOOL_DONE_LABELS: Record<string, string> = {
  // KB tools
  listArticlesInCategory: 'optibot.tool.doneSearchingKB',
  readArticle: 'optibot.tool.doneReadingArticle',
  searchArticles: 'optibot.tool.doneSearchingArticles',
  // Screen tools
  getScreensSummary: 'optibot.tool.doneGettingScreensSummary',
  listScreens: 'optibot.tool.doneLookingUpScreens',
  getScreen: 'optibot.tool.doneGettingScreenDetails',
  searchScreens: 'optibot.tool.doneSearchingScreens',
  // Asset tools
  listAssets: 'optibot.tool.doneLookingUpAssets',
  getAsset: 'optibot.tool.doneGettingAssetDetails',
  searchAssets: 'optibot.tool.doneSearchingAssets',
  // Playlist tools
  listPlaylists: 'optibot.tool.doneLookingUpPlaylists',
  getPlaylist: 'optibot.tool.doneGettingPlaylistDetails',
  searchPlaylists: 'optibot.tool.doneSearchingPlaylists',
  // Schedule tools
  listSchedules: 'optibot.tool.doneLookingUpSchedules',
  getSchedule: 'optibot.tool.doneGettingScheduleDetails',
  searchSchedules: 'optibot.tool.doneSearchingSchedules',
  // Account tools
  getMyProfile: 'optibot.tool.doneGettingProfile',
  listTeamMembers: 'optibot.tool.doneLookingUpTeamMembers',
  listTeams: 'optibot.tool.doneLookingUpTeams',
  getUserPreferences: 'optibot.tool.doneGettingPreferences',
  listIntegrations: 'optibot.tool.doneLookingUpIntegrations',
  // Admin tools
  getSamlConfig: 'optibot.tool.doneCheckingSSOConfig',
  listMonitoringAlerts: 'optibot.tool.doneLookingUpAlerts',
  getMonitoringAlert: 'optibot.tool.doneGettingAlertDetails',
  listProvisioningTemplates: 'optibot.tool.doneLookingUpTemplates',
  listOperationalSchedules: 'optibot.tool.doneLookingUpOpsSchedules',
  // Feed tools
  searchFeeds: 'optibot.tool.doneSearchingFeeds',
  listFeedCategories: 'optibot.tool.doneLookingUpFeedCategories',
  // Folder/group tools
  listFolders: 'optibot.tool.doneLookingUpFolders',
  getFolderInfo: 'optibot.tool.doneGettingFolderDetails',
  searchFolders: 'optibot.tool.doneSearchingFolders',
  // MDM ops tools
  getDeviceInfo: 'optibot.tool.doneGettingDeviceInfo',
  getDeviceStorage: 'optibot.tool.doneCheckingStorage',
  identifyScreen: 'optibot.tool.doneIdentifyingScreen',
  // Location tools
  listLocations: 'optibot.tool.doneLookingUpLocations',
  getLocationDevices: 'optibot.tool.doneGettingLocationDevices',
  // App discovery tools
  listAppTypes: 'optibot.tool.doneBrowsingApps',
  getAppTypeInfo: 'optibot.tool.doneGettingAppDetails',
  // Template tools
  searchTemplates: 'optibot.tool.doneBrowsingTemplates',
  // AI Designer tools
  generateDesign: 'optibot.tool.doneGeneratingDesign',
  // Billing tools
  getBillingOverview: 'optibot.tool.doneCheckingBilling',
  listInvoices: 'optibot.tool.doneLookingUpInvoices',
  listUnpaidInvoices: 'optibot.tool.doneCheckingUnpaidInvoices',
  getPaymentMethod: 'optibot.tool.doneCheckingPaymentMethod',
  listAddOns: 'optibot.tool.doneLookingUpAddOns',
  getCustomerCoupon: 'optibot.tool.doneCheckingCoupon',
  listTaxIds: 'optibot.tool.doneLookingUpTaxIds',
  listSubAccounts: 'optibot.tool.doneLookingUpSubAccounts',
  listPlanTiers: 'optibot.tool.doneLookingUpPlanTiers',
  getAddOnCatalog: 'optibot.tool.doneLookingUpAddOnCatalog',
  // Billing write tools (Tier 2)
  updateBillingEmail: 'optibot.tool.doneUpdatingBillingEmail',
  updateBillingAddress: 'optibot.tool.doneUpdatingBillingAddress',
  sendInvoiceByEmail: 'optibot.tool.doneSendingInvoice',
  addTaxId: 'optibot.tool.doneAddingTaxId',
  deleteTaxId: 'optibot.tool.doneDeletingTaxId',
  // Billing write tools (Tier 3)
  getSubscriptionChangePreview: 'optibot.tool.donePreviewingSubscriptionChange',
  increaseScreenCount: 'optibot.tool.doneIncreasingScreenCount',
  decreaseScreenCount: 'optibot.tool.doneDecreasingScreenCount',
  changeBillingInterval: 'optibot.tool.doneChangingBillingInterval',
  switchPlanTier: 'optibot.tool.doneSwitchingPlanTier',
  buyAddOn: 'optibot.tool.donePurchasingAddOn',
  startAddOnTrial: 'optibot.tool.doneStartingAddOnTrial',
  cancelAddOnTrial: 'optibot.tool.doneCancellingAddOnTrial',
  resumeSubscription: 'optibot.tool.doneResumingSubscription',
  payUnpaidInvoices: 'optibot.tool.donePayingInvoices',
};

/**
 * Returns an i18n key for the completed tool label.
 * Falls back to 'optibot.tool.done' for unknown tools.
 */
export function getToolDoneLabel(toolName: string): string {
  return TOOL_DONE_LABELS[toolName] ?? 'optibot.tool.done';
}

/**
 * Returns true if the tool result is an error.
 */
export function isErrorResult(result: unknown): result is { error: string } {
  return (
    typeof result === 'object' &&
    result !== null &&
    'error' in result &&
    typeof (result as Record<string, unknown>).error === 'string'
  );
}
